// Product page and homepage reviews from Judge.me's widget endpoint, the same request its own widget makes.

const ENDPOINT = 'https://cdn.judge.me/reviews/reviews_for_widget';
const PHOTO_HOST = 'https://review-images.judgeme.com/';
const TIMEOUT_MS = 8000;
const LIST_SHOWN = 6;
const HOME_TILES = 10;
// Judge.me's own badge text (Jasen chose this badge, Oct 16): it discloses that the review earned a thank-you code
const REWARD_TEXT = 'Review earned a reward for future purchase';
const dateFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const isoDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// children go in as text, never HTML: nothing from Judge.me is ever inserted as markup
function el(tag, attributes = {}, ...children) {
  const node = document.createElement(tag);
  for (const [name, value] of Object.entries(attributes)) {
    if (value === false || value == null) continue;
    if (name === 'class') node.className = value;
    else node.setAttribute(name, value === true ? '' : value);
  }
  node.append(...children.filter(child => child != null && child !== false));
  return node;
}

const words = (text, count) => {
  const list = text.split(/\s+/).filter(Boolean);
  return list.length > count ? { text: `${list.slice(0, count).join(' ').replace(/[\s,;:.!?—–-]+$/, '')}…`, cut: true } : { text, cut: false };
};

function photoAt(url, width) {
  const resized = new URL(url);
  resized.searchParams.set('width', String(width));
  return resized.href;
}

// One line per photo: a Judge.me file id (the digits before "__" in its address) or the whole address.
function hiddenPhotos(text) {
  return (text || '').split(/\r?\n/).map(line => line.trim()).filter(Boolean);
}
const isHidden = (url, hidden) => hidden.some(token => (token.startsWith('http') ? url.split('?')[0] === token.split('?')[0] : url.includes(`/${token}__`)));

function parseReviews(html) {
  const page = new DOMParser().parseFromString(html, 'text/html');
  // a review without a score is left out rather than shown with stars it may not have
  return [...page.querySelectorAll('.jdgm-rev')].filter(node => Number(node.querySelector('.jdgm-rev__rating')?.dataset.score) >= 1).map(node => {
    const time = node.querySelector('time[datetime]');
    const date = time ? new Date(time.getAttribute('datetime')) : null;
    const paragraphs = [...node.querySelectorAll('.jdgm-rev__body p')].map(p => p.textContent.trim()).filter(Boolean);
    const body = node.querySelector('.jdgm-rev__body')?.textContent.trim();
    return {
      rating: Math.min(5, Math.round(Number(node.querySelector('.jdgm-rev__rating').dataset.score))),
      verified: node.dataset.verifiedBuyer === 'true',
      rewarded: Boolean(node.querySelector('.jdgm-rev__transparency-badge[data-badge-type="review_earned_for_future_purchase"]')),
      date: date && !Number.isNaN(date.getTime()) ? date : null,
      author: node.querySelector('.jdgm-rev__author')?.textContent.trim() || 'Anonymous',
      paragraphs: paragraphs.length ? paragraphs : body ? [body] : [],
      photos: [...node.querySelectorAll('.jdgm-rev__pic-link')].map(link => link.getAttribute('href') || '').filter(href => href.startsWith(PHOTO_HOST))
    };
  });
}

// Judge.me returns at most 28 reviews a page, so 25 a page, paged to total_count.
async function loadReviews({ shop, productId, photosOnly = false, perPage = 25, maxPages = 8 }) {
  const reviews = [];
  for (let page = 1; page <= maxPages; page++) {
    const query = new URLSearchParams({ url: shop, shop_domain: shop, platform: 'shopify', product_id: String(productId), page: String(page), per_page: String(perPage) });
    if (photosOnly) query.set('sort_by', 'with_pictures');
    const response = await fetch(`${ENDPOINT}?${query}`, { signal: AbortSignal.timeout?.(TIMEOUT_MS) });
    if (!response.ok) throw new Error(`Judge.me reviews: HTTP ${response.status}`);
    const data = await response.json();
    const found = parseReviews(data.html || '');
    reviews.push(...found);
    if (photosOnly || !found.length || reviews.length >= Number(data.total_count || 0)) break;
  }
  return reviews;
}

function stars(rating) {
  return el('span', { class: 'rv-stars', role: 'img', 'aria-label': `${rating} out of 5 stars` }, '★'.repeat(rating), rating < 5 ? el('span', { class: 'rv-stars-off' }, '★'.repeat(5 - rating)) : null);
}
const reward = review => (review.rewarded ? el('span', { class: 'rv-reward' }, REWARD_TEXT) : null);
const who = review => el('p', { class: 'rv-who' }, el('strong', {}, review.author), review.verified ? ' · Verified buyer' : null, reward(review));
const reviewText = review => review.paragraphs.join(' ');
const whose = review => (review.author === 'Anonymous' ? "A customer's" : `${review.author}'s`);
// The same text gets posted on several products: one naming another product but not its own stays out of photo rows.
function misfiled(review, ownTitle, titles) {
  const text = reviewText(review).toLowerCase();
  const own = ownTitle.toLowerCase();
  if (text.includes(own)) return false;
  // a title inside this product's own ("OG Stally" in "OG Stally Crimson") is this product line, not another product
  return titles.some(title => title && !own.includes(title.toLowerCase()) && text.includes(title.toLowerCase()));
}

// The dialog shows every photo, hidden ones included: the hide list only governs photo rows.
function openReview(dialog, review, product) {
  const body = dialog.querySelector('[data-review-body]');
  // replaceChildren prints a null as the text "null", so the parts a review may lack are dropped first, as el() does
  body.replaceChildren(...[
    review.photos.length ? el('div', { class: 'rv-dlg-photos' }, ...review.photos.map((src, index) => el('img', { src: photoAt(src, 1000), alt: `${whose(review)} photo ${index + 1}`, loading: index ? 'lazy' : false }))) : null,
    stars(review.rating),
    ...review.paragraphs.map(text => el('p', { class: 'rv-dlg-text' }, text)),
    el('p', { class: 'rv-who' }, el('strong', {}, review.author), review.verified ? ' · Verified buyer' : null, review.date ? ' · ' : null, review.date ? el('time', { datetime: isoDate(review.date) }, dateFormat.format(review.date)) : null, reward(review)),
    product ? chip(product) : null
  ].filter(child => child != null));
  dialog.setAttribute('aria-label', `Review by ${review.author}`);
  dialog.showModal();
}

function chip(product) {
  return el('a', { class: 'rv-chip', href: product.url },
    product.image ? el('img', { src: product.image, alt: '', width: '44', height: '55', loading: 'lazy' }) : el('span', { class: 'rv-chip-blank' }),
    el('span', { class: 'rv-chip-name' }, el('b', {}, product.title), ' ', product.price), ' ',
    el('span', { class: 'rv-chip-go' }, 'Shop'));
}

function photoTile(review, src, dialog, { quoteWords, product, label }) {
  const quote = words(reviewText(review), quoteWords);
  const open = el('button', { class: 'rv-photo', type: 'button', 'aria-label': label },
    el('img', { src: photoAt(src, 800), alt: '', width: '800', height: '1000', loading: 'lazy', decoding: 'async' }));
  open.addEventListener('click', () => openReview(dialog, review, product));
  return el('li', { class: 'rv-tile' }, open,
    el('div', { class: 'rv-tile-text' }, stars(review.rating), el('p', { class: 'rv-quote' }, `“${quote.text}”`), who(review)),
    product ? chip(product) : null);
}

// A backdrop click closes it through closedby="any" (as the product gallery does); this wires the close button.
function wireClose(dialog) {
  if (dialog.dataset.wired) return;
  dialog.dataset.wired = 'true';
  dialog.addEventListener('click', event => {
    if (event.target.closest('[data-close]')) dialog.close();
  });
}

function scrollRow(row, previous, next) {
  const step = () => (row.firstElementChild?.getBoundingClientRect().width || row.clientWidth) + parseFloat(getComputedStyle(row).columnGap || '0');
  const behavior = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth');
  // aria-disabled, not disabled: a disabled button that has focus drops it to the page
  const update = () => {
    const end = row.scrollWidth - row.clientWidth - 2;
    previous.setAttribute('aria-disabled', String(row.scrollLeft <= 2));
    next.setAttribute('aria-disabled', String(row.scrollLeft >= end));
    previous.closest('[data-arrows]').hidden = end <= 0;
  };
  const move = (button, sign) => button.addEventListener('click', () => {
    if (button.getAttribute('aria-disabled') !== 'true') row.scrollBy({ left: sign * step(), behavior: behavior() });
  });
  move(previous, -1);
  move(next, 1);
  // Chrome scrolls a focused control into view only when wholly hidden, so a peeking tile's ring stays clipped: bring
  // the whole tile in (scroll-padding keeps the ring). Keyboard focus only: scrolling on a mouse press moves the tile
  // and the click misses. Start, not nearest: the row snaps to tile starts, and a part-way scroll snaps back.
  row.addEventListener('focusin', event => {
    const tile = event.target.closest('.rv-tile');
    if (!tile || !event.target.matches(':focus-visible')) return;
    const t = tile.getBoundingClientRect(), r = row.getBoundingClientRect();
    if (t.left < r.left + 8 || t.right > r.right - 8) tile.scrollIntoView({ inline: 'start', block: 'nearest', behavior: behavior() });
  });
  // --rv-photo-mid on the row's parent sets the homepage arrows level with the photos' middle (stally-rd-reviews.css)
  const place = () => {
    const photo = row.querySelector('.rv-photo');
    if (photo) row.parentElement.style.setProperty('--rv-photo-mid', `${photo.offsetTop + photo.offsetHeight / 2}px`);
  };
  row.addEventListener('scroll', update, { passive: true });
  new ResizeObserver(() => { update(); place(); }).observe(row);
  update();
  place();
}

// Product page
// Judge.me's widget instead of ours: its region gets its name back (taken away while its parts are hidden).
function showJudgeme(root, error) {
  root.classList.remove('rv-js');
  const region = root.querySelector('.rd-judgeme');
  if (region?.dataset.label) region.setAttribute('aria-label', region.dataset.label);
  if (error) console.warn('Reviews: showing Judge.me\'s widget instead.', error);
}

// After a fallback, from the Write a review button (hidden by then) or the rating line's link: Judge.me's own link
// takes focus if it can (Judge.me may still hide it), else its region, so focus never drops to the page.
function focusJudgeme(root) {
  const link = root.querySelector('.jdgm-write-rev-link');
  link?.focus();
  if (link && document.activeElement === link) return;
  const region = root.querySelector('.rd-judgeme');
  region.tabIndex = -1;
  region.focus();
}

// Judge.me builds its form a moment after its widget comes on screen and ignores clicks before that: wait up to 4 s.
// focusLink focuses its link first (display: none until then), so closing the form returns there. The link is
// <a href="javascript:void(0)" target="_blank"> (an empty tab), and Judge.me ignores an already-cancelled click,
// so the cancel runs last, on window, after its handler.
async function openJudgemeForm(root, { focusLink = false } = {}) {
  for (let i = 0; i < 40 && !document.querySelector('.jdgm-write-review-modal'); i++) await new Promise(done => setTimeout(done, 100));
  // looked up after the wait: Judge.me may draw or redraw its link meanwhile
  const link = root.querySelector('.jdgm-write-rev-link');
  if (!link || !document.querySelector('.jdgm-write-review-modal')) return false;
  if (focusLink) link.focus({ preventScroll: true });
  const cancel = event => { if (event.target === link) event.preventDefault(); };
  window.addEventListener('click', cancel);
  link.click();
  window.removeEventListener('click', cancel);
  return true;
}

// Write a review opens Judge.me's form from its hidden widget; wired at once, so it works before the reviews load.
function wireWrite(root) {
  const write = root.querySelector('[data-write]');
  if (!write) return;
  write.onclick = async () => {
    if (write.getAttribute('aria-busy') === 'true') return;
    write.setAttribute('aria-busy', 'true');
    const opened = await openJudgemeForm(root);
    write.removeAttribute('aria-busy');
    if (opened) return;
    showJudgeme(root, new Error('Judge.me form did not load'));
    focusJudgeme(root);
  };
}

// The rating's "write a review" link (sections/stally-rd-product.liquid) scrolls before pressing: Judge.me builds its
// form only once its widget is on screen. Focus moves to the button first, so closing the form returns to it. Once a
// product with no reviews fell back to Judge.me's widget, it scrolls to the widget's top: Judge.me shows its own link
// only once that top has been on screen.
// One document handler, added once (this module runs once a page), so editor section reloads never double it.
function wireWriteLinks() {
  document.addEventListener('click', async event => {
    const link = event.target.closest?.('[data-write-link]');
    const root = link && document.querySelector('[data-reviews="product"], [data-reviews="none"]');
    if (!root) return;
    const ours = root.classList.contains('rv-js') && root.querySelector('[data-write]');
    const target = ours || (root.dataset.reviews === 'none' && root.querySelector('.rd-judgeme'));
    if (!target) return;
    event.preventDefault();
    const settled = new Promise(done => { window.addEventListener('scrollend', done, { once: true }); setTimeout(done, 1200); });
    target.scrollIntoView({ block: ours ? 'center' : 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    await settled;
    if (ours) {
      ours.focus({ preventScroll: true });
      ours.click();
      return;
    }
    if (await openJudgemeForm(root, { focusLink: true })) return;
    focusJudgeme(root);
  });
}

async function initProductReviews(root) {
  const fallback = error => showJudgeme(root, error);
  let reviews;
  try {
    reviews = await loadReviews({ shop: root.dataset.shop, productId: root.dataset.productId });
  } catch (error) { fallback(error); return; }
  if (!reviews.length) { fallback(new Error('no reviews parsed')); return; }
  reviews.sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));
  const hidden = hiddenPhotos(root.dataset.hide);
  const dialog = root.querySelector('.rv-dialog');
  wireClose(dialog);

  const titles = (root.dataset.titles || '').split('|');
  const banded = reviews.filter(review => !misfiled(review, root.dataset.title || '', titles))
    .map(review => ({ review, src: review.photos.find(photo => !isHidden(photo, hidden)) })).filter(item => item.src);
  const band = root.querySelector('[data-band]');
  const row = root.querySelector('[data-row]');
  row.dataset.count = String(banded.length);
  row.toggleAttribute('data-scroll', banded.length > 4);
  row.replaceChildren(...banded.map(({ review, src }) => photoTile(review, src, dialog, { quoteWords: 14, label: `${whose(review)} photo: read the review` })));
  band.dataset.photos = String(banded.length);
  if (banded.length) {
    root.querySelector('[data-heading]').textContent = root.dataset.bandHeading;
    scrollRow(row, root.querySelector('[data-prev]'), root.querySelector('[data-next]'));
  }

  const written = reviews.filter(review => !banded.some(item => item.review === review));
  const list = root.querySelector('[data-list]');
  list.replaceChildren(...written.map((review, index) => {
    const text = words(reviewText(review), 38);
    const opener = (label, child) => {
      const button = el('button', { class: child ? 'rv-thumb' : 'rv-more-text', type: 'button', 'aria-label': label }, child || 'Read more');
      button.addEventListener('click', () => openReview(dialog, review));
      return button;
    };
    return el('li', { class: 'rv-item', hidden: index >= LIST_SHOWN, tabindex: '-1' },
      el('div', { class: 'rv-item-top' }, stars(review.rating), review.date ? el('time', { datetime: isoDate(review.date) }, dateFormat.format(review.date)) : null),
      review.photos.length ? opener(`${whose(review)} photo: read the review`, el('img', { src: photoAt(review.photos[0], 160), alt: '', width: '64', height: '80', loading: 'lazy' })) : null,
      el('p', { class: 'rv-text' }, text.text), text.cut ? opener(`Read more: ${whose(review)} review`) : null, who(review));
  }));
  const more = root.querySelector('[data-more]');
  const left = written.length - LIST_SHOWN;
  more.hidden = left <= 0;
  more.textContent = `Show ${left} more ${left === 1 ? 'review' : 'reviews'}`;
  more.onclick = () => {
    const first = list.querySelector('.rv-item[hidden]');
    list.querySelectorAll('.rv-item[hidden]').forEach(item => { item.hidden = false; });
    more.hidden = true;
    first?.focus();
  };
  root.querySelector('[data-written]').hidden = !written.length;
  root.dataset.ready = 'true';
}

// Homepage

async function initHomeReviews(root) {
  const products = JSON.parse(root.querySelector('[data-review-products]')?.textContent || '[]');
  const row = root.querySelector('[data-row]');
  if (!products.length || !row) return;
  const hidden = hiddenPhotos(root.dataset.hide);
  const dialog = root.querySelector('.rv-dialog');
  wireClose(dialog);
  const results = await Promise.allSettled(products.map(product => loadReviews({ shop: root.dataset.shop, productId: product.id, photosOnly: true, perPage: 3 })
    .then(reviews => reviews.map(review => ({ review, product })))));
  const candidates = results.filter(result => result.status === 'fulfilled').flatMap(result => result.value)
    .map(item => ({ ...item, src: item.review.photos.find(photo => !isHidden(photo, hidden)) }))
    .filter(item => item.src && reviewText(item.review) && !misfiled(item.review, item.product.title, (root.dataset.titles || '').split('|')))
    .sort((a, b) => (b.review.date?.getTime() || 0) - (a.review.date?.getTime() || 0));
  const usedProducts = new Set(), usedPeople = new Set();
  const tiles = [];
  for (const item of candidates) {
    const person = item.review.author === 'Anonymous' ? null : item.review.author;
    if (usedProducts.has(item.product.id) || (person && usedPeople.has(person))) continue;
    usedProducts.add(item.product.id);
    if (person) usedPeople.add(person);
    tiles.push(item);
    if (tiles.length === HOME_TILES) break;
  }
  if (!tiles.length) {
    root.querySelector('[data-rail]')?.remove();
    if (results.some(result => result.status === 'rejected')) console.warn('Reviews: Judge.me photo reviews unavailable.');
    return;
  }
  row.replaceChildren(...tiles.map(({ review, product, src }) => photoTile(review, src, dialog, { quoteWords: 16, product, label: `${whose(review)} photo, ${product.title}: read the review` })));
  root.querySelector('[data-rail]').hidden = false;
  scrollRow(row, root.querySelector('[data-prev]'), root.querySelector('[data-next]'));
  root.dataset.ready = 'true';
}

function start(root = document) {
  root.querySelectorAll('[data-reviews="product"], [data-reviews="none"]').forEach(section => {
    // Judge.me's widget is the fallback: hidden now with its region's name (it shows nothing while hidden). The
    // reviews load after the page does, so they never hold up the product's own photos.
    section.classList.add('rv-js');
    const region = section.querySelector('.rd-judgeme');
    if (region?.hasAttribute('aria-label')) { region.dataset.label = region.getAttribute('aria-label'); region.removeAttribute('aria-label'); }
    wireWrite(section);
    if (section.dataset.reviews === 'none') return;
    const run = () => initProductReviews(section);
    if (document.readyState === 'complete') run(); else window.addEventListener('load', run, { once: true });
  });
  root.querySelectorAll('[data-reviews="home"]').forEach(section => {
    const watcher = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      watcher.disconnect();
      initHomeReviews(section);
    }, { rootMargin: '1000px 0px' });
    watcher.observe(section);
  });
}

start();
wireWriteLinks();
document.addEventListener('shopify:section:load', event => start(event.target));
